<template>
  <div class="container-login text-left p-4">
    <h4 v-show="show !== 'initial'" class="text-landing semibold" style="cursor: pointer" @click="handleBackClick()">
      <i class="fa-solid fa-chevron-left me-2 d-inline"></i>
      {{ formTitle }}
    </h4>
    <template v-if="show === 'initial'">
      <h4 class="text-landing semibold title pb-3">{{ $t('home.form.personalInformation.iniciarSesionRegistrarReservar') }}</h4>

      <span class="title-field text-landing mb-2">
        {{ $t('home.form.personalInformation.emailLogin') }}
      </span>
      <form autocomplete="on" @submit.prevent="validateEmail(informacionLogin.email)">
        <input
          id="email"
          v-model="informacionLogin.email"
          type="email"
          class="form-control"
          :placeholder="$t('home.form.personalInformation.email')"
          autocomplete="email"
          name="email"
          required />
        <div class="my-3 text-center login-reserva__continuar">
          <MyGocuButton primary type="submit" :label="$t('botones.continuar')" />
        </div>
      </form>

      <div v-if="!isBodoSound && !isMandarinaSports && isGoogleOauthEnabled" class="divider">
        <span class="divider-text">o</span>
      </div>
      <div v-if="!isBodoSound && !isMandarinaSports && isGoogleOauthEnabled" class="text-center mt-1">
        <button
          class="btn btn-primary w-100 google-btn d-flex justify-content-center align-items-center m-auto"
          @click="doGoogleOauthLogin">
          <img src="@/assets/img/logo_google.svg" alt="Logo de Google" class="me-3" />
          <span class="text-landing semibold text-mobile">
            {{ $t('header.login.continuarGoogle') }}
          </span>
        </button>
      </div>
    </template>
    <template v-else-if="show === 'registerGoogle'">
      <RegistroGoogleForm
        :informacion-registro-google="informacionRegistroGoogle"
        @usuario-logueado="usuarioLogueadoHandler" />
    </template>
    <template v-else>
      <LoginForm
        :email="informacionLogin.email"
        :show-register="show === 'register'"
        @mostrar-login="mostrarLogin"
        @mostrar-recuperar-password="mostrarRecuperarPassword"
        @usuario-logueado="usuarioLogueadoHandler" />
    </template>
  </div>
</template>

<script setup>
  import MyGocuButton from '@/components/common/buttons/MyGocuButton.vue';
  import {
    alertAutoCloseWarningWithoutCallback,
    alertInfoHtml,
    alertWarning,
  } from '@/services/utils/AlertUtils';
  import { createToaster } from '@meforma/vue-toaster';
  import { computed, reactive, ref } from 'vue';
  import UsuarioApiService from '../../services/api/UsuarioApiService';
  import { controlErrorRegistrarLoginUsuario } from '@/services/utils/ErrorControl';
  import LoginForm from '@/components/form/LoginForm.vue';
  import { useI18n } from 'vue-i18n';
  import { googleSdkLoaded } from 'vue3-google-login';
  import { useLoadingStore } from '@/store_pinia/loadingStore';
  import { useUserStore } from '@/store_pinia/userStore';
  import { agenciaStoreModule } from '@/store_pinia/agencia';
  import { clearObject } from '@/helpers/utils';
  import RegistroGoogleForm from '@/components/form/RegistroGoogleForm.vue';

  const emit = defineEmits(['logueado', 'recordar']);

  const userStore = useUserStore();
  const loadingStore = useLoadingStore();
  const agenciaStore = agenciaStoreModule();
  const { t } = useI18n();
  const toaster = createToaster({ position: 'top-right' });

  const informacionLogin = reactive({
    email: null,
    password: null,
  });

  const informacionRegistroGoogle = reactive({
    email: null,
    nombre: null,
    apellidos: null,
    fechaNacimiento: null,
    telefono: null,
  });

  const show = ref('initial');
  const isBodoSound = ref(window.location.hostname.includes('bodosound'));
  const isMandarinaSports = ref(window.location.hostname.includes('mandarinasports'));
  const formTitle = ref(t('header.login.welcome'));

  const isGoogleOauthEnabled = computed(() => agenciaStore.getGoogleOauth);

  async function validateEmail(email) {
    try {
      loadingStore.show();
      const { exists, activo, activoPending } = await UsuarioApiService.validateEmail(email);
      if (exists && !activo) {
        if (activoPending) {
          await alertInfoHtml(t('header.login.solicitudEnRevision'));
        } else {
          await alertWarning(t('header.login.solicitudRechazada'));
        }
        return;
      }
      const vista = exists ? 'login' : 'register';
      show.value = vista;
      setFormTitle(vista);
    } catch (error) {
      console.error(error);
      show.value = 'register';
    } finally {
      loadingStore.hide();
    }
  }

  function usuarioLogueadoHandler() {
    emit('logueado');
  }

  function handleBackClick() {
    show.value = 'initial';
    setFormTitle();
  }

  function mostrarLogin() {
    setFormTitle('homeLogin');
    show.value = 'initial';
  }

  function mostrarRecuperarPassword(email) {
    emit('recordar', email);
  }

  function setFormTitle(formType) {
    switch (formType) {
      case 'homeLogin':
        formTitle.value = t('header.login.welcome');
        break;
      case 'login':
        formTitle.value = t('home.form.personalInformation.welcomeBack');
        break;
      case 'register':
        formTitle.value = t('header.login.tusDatos');
        break;
      case 'registerGoogle':
        formTitle.value = t('header.login.tusDatos');
        break;
      case 'resetPassword':
        formTitle.value = t('header.login.restablecerPassword');
        break;
      default:
        formTitle.value = t('header.login.welcome');
        break;
    }
  }

  function doGoogleOauthLogin() {
    googleSdkLoaded(google => {
      google.accounts.oauth2
        .initCodeClient({
          client_id: agenciaStore.getGoogleOauth,
          scope:
            'email profile openid https://www.googleapis.com/auth/user.birthday.read https://www.googleapis.com/auth/user.phonenumbers.read',
          callback: response => regAndLoginGoogle(response),
        })
        .requestCode();
    });
  }

  async function regAndLoginGoogle(callbackResponse) {
    try {
      loadingStore.show();
      const response = await UsuarioApiService.loguearGoogle(callbackResponse);

      if (response.token != null) {
        userStore.login(response.token);
        toaster.success(t('header.login.msg.loginSuccess'));
        emit('logueado');
      } else if (
        response.nombre != null &&
        response.apellidos != null &&
        response.email != null &&
        response.fechaNacimiento != null &&
        response.telefono != null
      ) {
        const res = await UsuarioApiService.registrarYLoguearGoogle(response);
        if (res.token == null) {
          alertAutoCloseWarningWithoutCallback(3000, t('errors.inesperado'));
        } else {
          userStore.login(res.token);
          toaster.success(t('header.login.msg.loginSuccess'));
          emit('logueado');
        }
      } else {
        clearObject(informacionRegistroGoogle);
        Object.assign(informacionRegistroGoogle, response);

        show.value = 'registerGoogle';
        setFormTitle('registerGoogle');
      }
    } catch (error) {
      console.error(error);
      controlErrorRegistrarLoginUsuario(error);
    } finally {
      loadingStore.hide();
    }
  }
</script>

<style lang="scss" scoped>
  .container-login {
    border-radius: var(--bs-border-radius-xl);
    border: 1px solid rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.25);

    .login-reserva__continuar {
      :deep(.mg-btn-root),
      :deep(.mg-btn) {
        width: 100%;
      }
    }

    .title {
      font-size: 20px;
      font-weight: 500;
      letter-spacing: 0.309px;
    }

    .title-field {
      font-size: 14px;
      font-weight: 400;
    }

    .divider {
      text-align: center;
      margin: 10px 0;
      position: relative;
    }

    .divider-text {
      background-color: var(--landing-background-color, var(--bs-white));
      padding: 0 10px;
      position: relative;
      z-index: 1;
    }

    .divider::after {
      content: '';
      background: rgba(var(--landing-text-color-rgb, var(--blue-1-rgb)), 0.3);
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      height: 1px;
      z-index: 0;
    }
  }

  .login {
    max-width: 550px;
    margin: auto;
  }

  .register {
    max-width: 850px;
    margin: auto;
  }

  .cambio-vista {
    cursor: pointer;
    transition: 0.3s ease-in-out;
    color: var(--bs-white);

    &:hover {
      color: var(--bs-secondary);
    }
  }

  .cambio-vista-2 {
    cursor: pointer;
    transition: 0.3s ease-in-out;

    &:hover {
      color: var(--bs-secondary);
    }
  }

  .form-check-input:checked[type='checkbox'],
  .form-check-input[type='checkbox'] {
    border-radius: 0;
  }

  .google-btn {
    border: 1px solid var(--landing-text-color, var(--bs-primary)) !important;
    box-shadow: none !important;
    background-color: transparent !important;
    transition: all 0.3s ease;
    @media only screen and (max-width: 768px) {
      font-size: 14px !important;
    }
  }

  .google-btn:hover {
    background-color: var(--landing-text-color, var(--bs-primary)) !important;

    span {
      transition: all 0.5s ease;
      color: var(--landing-background-color, var(--bs-white)) !important;
    }
  }
</style>
